// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ## LabelCountsQuestion
// ### Question Class
//
// Include description of object & useful info here, please!
import type TimeSignature from '../uTheory.Notation/timeSignature';
import type { QuestionBankMeasureRhythm } from './rhythmQuestionProcessor';
import RhythmQuestionProcessor from './rhythmQuestionProcessor';
import type Music from '../uTheory.Notation/music';
import { getBeatSyllablesTemplate, getSyllable } from '../uTheory.Notation/rhythmicSolfegeSystems';

export default class LabelCountsQuestion extends RhythmQuestionProcessor {
  subdivsTemplate!: string[];

  /** 1 e & a system or takadimi system? */
  protected countMethod: '1e&a' | 'takadimi';

  /** Number of syllables per note/rest that should be included in answer */
  answer!: number[];

  isSmallSubdivision!: boolean;

  constructor(
    rhythmOrMusicStringOrObject: string | QuestionBankMeasureRhythm,
    questionNumber: number,
    countMethod: '1e&a' | 'takadimi'
  ) {
    super(rhythmOrMusicStringOrObject, questionNumber);
    this.countMethod = countMethod;
    // In superclass, display is set to 1/1, here we reset it to the correct time signature
    delete this.timeSignature.displayAs;

    // Sets answers and skills from music;
    this.setCountsFromMusic();
  }

  processQuestionString(rhythmOrMusicString: string): {
    music: Music;
    tempoRange: [number, number];
  } {
    const { music, tempoRange } = super.processQuestionString(rhythmOrMusicString);

    if (music.voices[0]!.measures.length > 1)
      throw new Error('Music cannot have more than one measure');
    return { music, tempoRange };
  }

  // set skills(val: string[]): {}

  get skills(): string[] {
    if (this.timeSignature.isSimple) {
      return this.isSmallSubdivision
        ? ['count_labeling_simple_small_subdivisions']
        : ['count_labeling_simple'];
    }
    return this.isSmallSubdivision
      ? ['count_labeling_compound_small_subdivisions']
      : ['count_labeling_compound'];
  }

  /**
   * From music, determines and returns number of subdivisions in a beat and set syllables template.
   * Also sets this.skills
   */
  protected setTemplateAndDetermineSubdivsNumber(timeSignature: TimeSignature, smallSub: boolean) {
    const beats = timeSignature.beatsPerMeasure;
    if (beats !== Math.floor(beats))
      throw new Error('This interactive cannot take music of mixed meter.');

    // Grab the appropriate syllables for 1 beat and set skills
    this.subdivsTemplate = getBeatSyllablesTemplate(
      this.countMethod,
      timeSignature.isSimple,
      smallSub
    );

    return this.subdivsTemplate.length;
  }

  getSyllable(syllableNumber: number): string {
    return getSyllable(
      syllableNumber,
      this.countMethod,
      this.timeSignature.isSimple,
      this.isSmallSubdivision
    );
  }

  /**
   * Sets the answer array of count for each note from a music object. Also set skills
   */
  protected setCountsFromMusic() {
    const music = this.music;
    const timeSig = music.getTimeSignature();
    if (!timeSig) {
      throw new Error("Couldn't get a Time Signature for the music object. ");
    }
    const beatFraction = timeSig.beatDuration.fraction;
    const nhs = music.voices[0]!.measures[0]!.noteHolders;
    const rhythmToBeatArr = nhs.map((nh) => {
      if (!nh.rhythm) {
        throw new Error("NoteHolder doesn't have any rhythm.");
      }
      const rhythmFraction = nh.rhythm.fraction;
      // rhythm to beat ratio
      const rhythmToBeat = rhythmFraction.divide(beatFraction).simplify();
      // console.log(rhythmToBeat);

      //  Determines whether small subdivisions are needed for this rhythm
      return { rhythmToBeat, smallSub: rhythmToBeat.denominator > 3 };
    });

    const smallSub = rhythmToBeatArr.reduce((total, obj) => total || obj.smallSub, false);
    this.isSmallSubdivision = smallSub;
    const subdivsNumber = this.setTemplateAndDetermineSubdivsNumber(
      music.getTimeSignature()!,
      smallSub
    );
    this.answer = rhythmToBeatArr.map((obj) => obj.rhythmToBeat.multiply(subdivsNumber).value());
  }
}
